// ---------------------------------------------------------------------------
// Accessify – Contrast Modes (Accessibility Feature)
// ---------------------------------------------------------------------------
//
// The UI exposes TWO honest high-contrast polarities: `light` (black on
// white) and `high` (white on black). Both genuinely raise contrast; they
// differ only in light/dark polarity. The older soft `dark` theme is NOT a
// contrast tool and is no longer offered as a chip, but its token stays for
// backward-compat with prefs that were saved as 'dark' before this change.
//
// Design principles:
//   1. CONTENT elements (text, headings, links, buttons, form controls) get
//      explicit color overrides — these are what users need to read.
//   2. STRUCTURAL elements (wrapper divs, sections, nav) get background only
//      via `html` and `body` — not individually. This prevents breaking
//      Framer/CSS-Grid/Flex layouts where divs use fit-content, overflow:hidden,
//      or fixed heights.
//   3. The widget itself (inside #accessify-root / Shadow DOM) is NEVER touched.
//   4. Focus indicators are defined in ALL modes, not just high contrast.
//   5. border-color is NOT globally overridden — only on elements that have
//      visible borders for content separation (tables, fieldsets, hr).
//
// Selector strategy:
//   - `:where()` for zero-specificity (site styles can still win if needed,
//     but `!important` ensures accessibility overrides take priority).
//   - `body :where(...)` scopes to page content.
//   - `body *:not(#accessify-root):not(#accessify-root *)` used only where
//     we need broad text color coverage.
//   - Framer-safe: no background-color on generic div/section/article — only
//     on html/body. Content readability comes from text color, not from
//     painting every container.
//
// ---------------------------------------------------------------------------

import type { FeatureModule, FeatureState } from '../types';

type ContrastMode = 'off' | 'light' | 'dark' | 'high';

// ---------------------------------------------------------------------------
// Token definitions — each mode is a self-contained color system
// ---------------------------------------------------------------------------

interface ContrastTokens {
  colorScheme: string;
  pageBg: string;
  text: string;
  textStrong: string;    // headings, bold
  link: string;
  linkVisited: string;
  focusRing: string;
  borderContent: string; // for tables, hr, fieldset — NOT every div
  inputBg: string;
  inputBorder: string;
  imgFilter: string;     // empty string = no filter
}

const TOKENS: Record<Exclude<ContrastMode, 'off'>, ContrastTokens> = {
  // Light polarity of HIGH contrast: pure black on pure white (~21:1), strong
  // blue links. This is the genuine high-contrast-light mode, not the old soft
  // cream "reading" theme — under a control labelled "Hoher Kontrast", the
  // light option must be just as uncompromising as the dark one.
  light: {
    colorScheme: 'light',
    pageBg:       '#ffffff',
    text:         '#000000',
    textStrong:   '#000000',
    link:         '#0b3d91',
    linkVisited:  '#6b21a8',
    focusRing:    '#0b3d91',
    borderContent:'#000000',
    inputBg:      '#ffffff',
    inputBorder:  '#000000',
    imgFilter:    '',
  },
  dark: {
    colorScheme: 'dark',
    pageBg:       '#101418',
    text:         '#e8e4dc',
    textStrong:   '#ffffff',
    link:         '#7cc8f5',
    linkVisited:  '#b8a0e8',
    focusRing:    '#7cc8f5',
    borderContent:'#3a4a58',
    inputBg:      '#1a2028',
    inputBorder:  '#4a5a68',
    imgFilter:    'brightness(0.90) contrast(1.06)',
  },
  high: {
    colorScheme: 'dark',
    pageBg:       '#000000',
    text:         '#ffffff',
    textStrong:   '#ffffff',
    link:         '#ffff00',
    linkVisited:  '#ffff00',
    focusRing:    '#ffff00',
    borderContent:'#ffffff',
    inputBg:      '#000000',
    inputBorder:  '#ffffff',
    imgFilter:    'contrast(1.25) brightness(0.95)',
  },
};

// ---------------------------------------------------------------------------
// CSS generation — separated into logical layers
// ---------------------------------------------------------------------------

/** Elements to exclude from page-level contrast: the widget itself PLUS die
 * Accessify-Light-DOM-Overlays (Lesemaske, TTS-Leiste, Lupe, Skip-Link,
 * Toast — alle mit id="accessify-…"). Ohne die zweite Ausnahme machte
 * Layer 1 (`background-color: transparent !important`) die Masken-Overlays
 * unsichtbar: deren Inline-Hintergrund (normale Priorität) verliert gegen
 * Stylesheet-!important. */
const EX =
  ':not(#accessify-root):not(#accessify-root *)' +
  ':not([id^="accessify-"]):not([id^="accessify-"] *)';

function buildCSS(mode: Exclude<ContrastMode, 'off'>): string {
  const t = TOKENS[mode];
  const isHigh = mode === 'high';

  // ── Layer 1: Page background + strip all container backgrounds ──
  // Strategy: set pageBg ONLY on html/body, then make all other elements
  // transparent. This prevents opaque containers from hiding images on
  // Shopify/Framer sites (overlay divs, z-index stacking, etc.).
  // Form controls get their own bg back in Layer 4.
  const base = `
    html {
      color-scheme: ${t.colorScheme} !important;
    }
    html, body {
      background-color: ${t.pageBg} !important;
      color: ${t.text} !important;
    }
    body *${EX} {
      background-color: transparent !important;
      background-image: none !important;
    }
  `;

  // ── Layer 2: Text color on ALL content elements ──
  // Using body * ensures text inside any container is readable, without
  // painting the container's background (which would break layouts).
  // html/body get color in Layer 1 so text nodes that live directly in
  // body (rare but real on minimal themes) stay readable in high contrast.
  const textColor = `
    body *${EX} {
      color: ${t.text} !important;
    }
    body :where(h1, h2, h3, h4, h5, h6, strong, b, th)${EX} {
      color: ${t.textStrong} !important;
    }
  `;

  // ── Layer 3: Links — distinct color + underline for visibility ──
  const links = `
    body :where(a)${EX} {
      color: ${t.link} !important;
      text-decoration: underline !important;
      text-decoration-color: ${t.link} !important;
      ${isHigh ? 'text-decoration-thickness: 2px !important; text-underline-offset: 3px !important;' : ''}
    }
    body :where(a:visited)${EX} {
      color: ${t.linkVisited} !important;
    }
    body :where(a:hover, a:focus)${EX} {
      opacity: 0.85 !important;
    }
  `;

  // ── Layer 4: Form controls — explicit bg + border for readability ──
  // These get background because they ARE content, not structural wrappers.
  const forms = `
    body :where(input, textarea, select)${EX} {
      background-color: ${t.inputBg} !important;
      color: ${t.text} !important;
      border: 1px solid ${t.inputBorder} !important;
      box-shadow: none !important;
    }
    body :where(button, [role="button"])${EX} {
      color: ${t.text} !important;
      border-color: ${t.inputBorder} !important;
      ${isHigh ? `background-color: ${t.pageBg} !important; border: 2px solid ${t.inputBorder} !important;` : ''}
    }
  `;

  // ── Layer 5: Focus indicators — in ALL modes, stronger in high ──
  const focus = `
    body :where(a, button, input, textarea, select, [tabindex])${EX}:focus-visible {
      outline: ${isHigh ? '3px' : '2px'} solid ${t.focusRing} !important;
      outline-offset: 2px !important;
    }
  `;

  // ── Layer 6: Content borders — only on elements that visually separate ──
  // NOT on generic divs — that destroys Framer card layouts.
  const borders = `
    body :where(table, th, td, hr, fieldset)${EX} {
      border-color: ${t.borderContent} !important;
    }
  `;

  // ── Layer 7: Images — gentle filter, skip decorative SVG icons ──
  const images = t.imgFilter ? `
    body :where(img, video, picture)${EX} {
      filter: ${t.imgFilter} !important;
    }
  ` : '';

  // ── Layer 8: High contrast extras ──
  const highExtras = isHigh ? `
    body :where(::placeholder)${EX} {
      color: #aaaaaa !important;
      opacity: 1 !important;
    }
    body :where(::selection) {
      background: #ffff00 !important;
      color: #000000 !important;
    }
    body :where(mark)${EX} {
      background: #ffff00 !important;
      color: #000000 !important;
    }
  ` : '';

  return base + textColor + links + forms + focus + borders + images + highExtras;
}

// ---------------------------------------------------------------------------
// Module
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Inline-style override — Framer/Webflow/etc set background via inline style
// which beats stylesheet !important. We must strip them via JS and restore on
// deactivate.
// ---------------------------------------------------------------------------

const BACKUP_ATTR = 'data-a11y-bg';
const BACKUP_COLOR_ATTR = 'data-a11y-color';

/** Strip inline background from all elements (except widget + media) */
function stripInlineBackgrounds() {
  const els = document.querySelectorAll(
    'body *:not(#accessify-root):not(#accessify-root *):not([id^="accessify-"]):not([id^="accessify-"] *)'
  );
  for (const el of els) {
    const html = el as HTMLElement;
    if (html.tagName === 'IMG' || html.tagName === 'VIDEO' || html.tagName === 'PICTURE' ||
        html.tagName === 'CANVAS' || html.tagName === 'SVG') continue;
    // Skip form controls — they get their own bg in Layer 4
    if (html.tagName === 'INPUT' || html.tagName === 'TEXTAREA' || html.tagName === 'SELECT') continue;

    const inlineBg = html.style.background || html.style.backgroundColor || html.style.backgroundImage;
    if (!inlineBg) continue;

    // Back up original values so we can restore later
    if (!html.hasAttribute(BACKUP_ATTR)) {
      html.setAttribute(BACKUP_ATTR, JSON.stringify({
        bg: html.style.background,
        bgColor: html.style.backgroundColor,
        bgImage: html.style.backgroundImage,
      }));
    }
    html.style.setProperty('background', 'transparent', 'important');
    html.style.setProperty('background-color', 'transparent', 'important');
    html.style.setProperty('background-image', 'none', 'important');
  }
}

/** Restore original inline backgrounds */
function restoreInlineBackgrounds() {
  const els = document.querySelectorAll(`[${BACKUP_ATTR}]`);
  for (const el of els) {
    const html = el as HTMLElement;
    try {
      const saved = JSON.parse(html.getAttribute(BACKUP_ATTR) || '{}');
      html.style.background = saved.bg || '';
      html.style.backgroundColor = saved.bgColor || '';
      html.style.backgroundImage = saved.bgImage || '';
    } catch { /* ignore parse errors */ }
    html.removeAttribute(BACKUP_ATTR);
  }
}

/**
 * Strip inline `color` styles set by site CSS-in-JS (Framer, Webflow,
 * styled-components). Inline `color` beats stylesheet `!important`, so
 * without this, the Layer-2 text-color rules above would not actually win
 * on those sites — and the "high contrast" mode would render dark-grey text
 * on a black background, i.e. the exact bug R6-1 caught in the audit.
 *
 * We do NOT touch the widget root, media elements, or form controls (those
 * have their own color rules in Layer 4).
 */
function stripInlineColors(textColor: string) {
  const els = document.querySelectorAll(
    'body *:not(#accessify-root):not(#accessify-root *):not([id^="accessify-"]):not([id^="accessify-"] *)'
  );
  for (const el of els) {
    const html = el as HTMLElement;
    if (html.tagName === 'IMG' || html.tagName === 'VIDEO' || html.tagName === 'PICTURE' ||
        html.tagName === 'CANVAS' || html.tagName === 'SVG') continue;
    if (html.tagName === 'INPUT' || html.tagName === 'TEXTAREA' || html.tagName === 'SELECT') continue;

    const inlineColor = html.style.color;
    if (!inlineColor) continue;

    if (!html.hasAttribute(BACKUP_COLOR_ATTR)) {
      html.setAttribute(BACKUP_COLOR_ATTR, inlineColor);
    }
    html.style.setProperty('color', textColor, 'important');
  }
}

/** Restore original inline color values */
function restoreInlineColors() {
  const els = document.querySelectorAll(`[${BACKUP_COLOR_ATTR}]`);
  for (const el of els) {
    const html = el as HTMLElement;
    const saved = html.getAttribute(BACKUP_COLOR_ATTR) || '';
    html.style.color = saved;
    html.removeAttribute(BACKUP_COLOR_ATTR);
  }
}

// ---------------------------------------------------------------------------
// Module
// ---------------------------------------------------------------------------

export default function createContrastModule(): FeatureModule {
  let currentMode: ContrastMode = 'off';
  // Naming: `-styles` suffix matches the text-size + tts modules' pattern
  // (and the existing unit + e2e tests). The pre-R6-4 id was just
  // `accessify-contrast`, but staying inconsistent with the rest of the
  // codebase costs more in test maintenance than the rename does.
  const STYLE_ID = 'accessify-contrast-styles';
  const STORAGE_KEY = 'accessify-contrast-mode';

  function applyStyles(mode: ContrastMode) {
    let styleEl = document.getElementById(STYLE_ID);
    if (mode === 'off') {
      styleEl?.remove();
      restoreInlineBackgrounds();
      restoreInlineColors();
      return;
    }
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = buildCSS(mode);
    // Strip inline backgrounds AND colors AFTER CSS is applied — handles
    // Framer/Webflow themes whose inline styles otherwise beat our stylesheet.
    stripInlineBackgrounds();
    stripInlineColors(TOKENS[mode].text);
  }

  function activate() {
    const saved = localStorage.getItem(STORAGE_KEY) as ContrastMode;
    if (saved === 'light' || saved === 'dark' || saved === 'high') {
      currentMode = saved;
    } else {
      // No stored choice: pick the polarity from the OS preference. The soft
      // 'dark' theme is no longer a default — both fallbacks are real high
      // contrast, light or dark.
      const prefersDark =
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches;
      currentMode = prefersDark ? 'high' : 'light';
    }
    applyStyles(currentMode);
  }

  function deactivate() {
    currentMode = 'off';
    applyStyles('off');
    localStorage.removeItem(STORAGE_KEY);
  }

  return {
    id: 'contrast',
    name: () => 'Contrast',
    description: 'Increase contrast for better readability',
    icon: 'contrast',
    category: 'visual',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'contrast', enabled: currentMode !== 'off', value: currentMode }),
    setState: (mode: ContrastMode) => {
      currentMode = mode === 'light' || mode === 'dark' || mode === 'high' ? mode : 'off';
      applyStyles(currentMode);
      if (currentMode === 'off') {
        localStorage.removeItem(STORAGE_KEY);
      } else {
        localStorage.setItem(STORAGE_KEY, currentMode);
      }
    },
  };
}
